import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * Allows safe HTML tags for rich text content while removing malicious scripts
 *
 * @param {string} html - Raw HTML content from rich text editor
 * @returns {string} - Sanitized HTML safe for storage and display
 */
export const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Configure DOMPurify to allow common rich text formatting tags
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'span', 'div'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel'
    ],
    ALLOWED_STYLES: {
      '*': {
        'color': [/^#[0-9a-fA-F]{3,6}$/, /^rgb\(/],
        'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
        'font-size': [/^[0-9]+(px|em|rem|%)$/],
        'font-weight': [/^bold$/, /^normal$/, /^[0-9]{3}$/],
        'font-style': [/^italic$/, /^normal$/],
        'text-decoration': [/^underline$/, /^line-through$/, /^none$/]
      }
    },
    // Force links to open in new window and set noopener for security
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ADD_ATTR: ['target'],
    // Remove any data: URIs for images to prevent data exfiltration
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  };

  // Sanitize and return
  const clean = DOMPurify.sanitize(html, config);

  // Additional safety: ensure all external links have rel="noopener noreferrer"
  return clean.replace(
    /<a\s+([^>]*\s+)?href="https?:\/\/[^"]*"([^>]*)>/gi,
    (match) => {
      if (!match.includes('rel=')) {
        return match.replace('>', ' rel="noopener noreferrer">');
      } else if (!match.includes('noopener')) {
        return match.replace(/rel="([^"]*)"/, 'rel="$1 noopener noreferrer"');
      }
      return match;
    }
  );
};

/**
 * Sanitize plain text input to prevent injection attacks
 * Removes HTML tags and special characters that could be dangerous
 *
 * @param {string} text - Raw text input
 * @returns {string} - Sanitized plain text
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Remove any HTML tags
  let sanitized = text.replace(/<[^>]*>/g, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  return sanitized;
};

/**
 * Sanitize string for use in SQL-like searches
 * Escapes special characters that could be used in injection attacks
 *
 * @param {string} searchTerm - Search input from user
 * @returns {string} - Sanitized search term
 */
export const sanitizeSearchTerm = (searchTerm) => {
  if (!searchTerm || typeof searchTerm !== 'string') {
    return '';
  }

  // Remove leading/trailing whitespace
  let sanitized = searchTerm.trim();

  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  // Limit length to prevent DOS attacks via huge search strings
  const MAX_SEARCH_LENGTH = 255;
  if (sanitized.length > MAX_SEARCH_LENGTH) {
    sanitized = sanitized.substring(0, MAX_SEARCH_LENGTH);
  }

  return sanitized;
};

/**
 * Sanitize URL to prevent javascript: and data: protocols
 *
 * @param {string} url - URL input from user
 * @returns {string} - Sanitized URL or empty string if dangerous
 */
export const sanitizeURL = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Trim whitespace
  const trimmed = url.trim();

  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
  const lowerURL = trimmed.toLowerCase();

  for (const protocol of dangerousProtocols) {
    if (lowerURL.startsWith(protocol)) {
      console.warn(`Blocked dangerous URL protocol: ${protocol}`);
      return '';
    }
  }

  // Only allow http, https, mailto, tel
  const allowedProtocolPattern = /^(https?|mailto|tel):/i;
  const relativePattern = /^(\/|\.\/|\.\.\/)/;

  // If it has a protocol, it must be an allowed one
  if (trimmed.includes(':') && !allowedProtocolPattern.test(trimmed)) {
    console.warn(`Blocked URL with disallowed protocol: ${trimmed}`);
    return '';
  }

  // Allow relative URLs and URLs with allowed protocols
  if (relativePattern.test(trimmed) || allowedProtocolPattern.test(trimmed) || !trimmed.includes(':')) {
    return trimmed;
  }

  return '';
};

/**
 * Sanitize object deeply - useful for sanitizing form data before API calls
 *
 * @param {Object} obj - Object to sanitize
 * @param {Object} options - Sanitization options
 * @returns {Object} - Sanitized object
 */
export const sanitizeObject = (obj, options = {}) => {
  const {
    htmlFields = [], // Fields that contain HTML (will use sanitizeHTML)
    urlFields = [],  // Fields that contain URLs (will use sanitizeURL)
    skipFields = []  // Fields to skip sanitization (e.g., encrypted data)
  } = options;

  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, options));
  }

  const sanitized = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip fields that shouldn't be sanitized
    if (skipFields.includes(key)) {
      sanitized[key] = value;
      continue;
    }

    // Sanitize based on field type
    if (value === null || value === undefined) {
      sanitized[key] = value;
    } else if (htmlFields.includes(key) && typeof value === 'string') {
      sanitized[key] = sanitizeHTML(value);
    } else if (urlFields.includes(key) && typeof value === 'string') {
      sanitized[key] = sanitizeURL(value);
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeText(value);
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeObject(value, options);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Validate and sanitize email address
 *
 * @param {string} email - Email address to validate
 * @returns {string} - Sanitized email or empty string if invalid
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const trimmed = email.trim().toLowerCase();

  // Basic email validation regex
  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

  if (!emailRegex.test(trimmed)) {
    return trimmed; // Return as-is for form validation to catch
  }

  // Remove any potential HTML/script tags
  return trimmed.replace(/<[^>]*>/g, '');
};

/**
 * Validate and sanitize phone number
 *
 * @param {string} phone - Phone number to sanitize
 * @returns {string} - Sanitized phone number
 */
export const sanitizePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  // Remove any HTML tags
  let sanitized = phone.replace(/<[^>]*>/g, '');

  // Allow only numbers, spaces, hyphens, parentheses, and plus sign
  sanitized = sanitized.replace(/[^0-9\s\-()+ ]/g, '');

  return sanitized.trim();
};

/**
 * Sanitize numeric input
 *
 * @param {string|number} value - Numeric value to sanitize
 * @returns {number|null} - Sanitized number or null if invalid
 */
export const sanitizeNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Convert to number
  const num = Number(value);

  // Check if valid number
  if (isNaN(num) || !isFinite(num)) {
    return null;
  }

  return num;
};

// Export default object with all sanitization functions
export default {
  sanitizeHTML,
  sanitizeText,
  sanitizeSearchTerm,
  sanitizeURL,
  sanitizeObject,
  sanitizeEmail,
  sanitizePhone,
  sanitizeNumber
};
